"use client";

import { useEffect, ReactNode, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/providers/auth-provider";
import { useOnboarding } from "@/providers/onboarding-provider";
import { Loading } from "@/components/ui/loading";
import { GradientSurface } from "@/components/effects";

interface OnboardingGuardProps {
  children: ReactNode;
}

// Routes that don't require onboarding
const PUBLIC_ROUTES = [
  "/login",
  "/register",
  "/privacy",
  "/terms",
  "/onboarding", // Allow access to onboarding itself
  "/", // Landing page
];

// Routes that should redirect to onboarding if not completed
const PROTECTED_ROUTES = [
  "/local-links-v1",
  "/local-links-v1/matchmaking",
  "/local-links-v1/leagues",
  "/local-links-v1/messages",
  "/local-links-v1/notifications",
  "/local-links-v1/settings",
];

// Maximum time to wait for loading states before showing content anyway
const LOADING_TIMEOUT_MS = 5000;
// Time before showing extended loading message
const EXTENDED_LOADING_MS = 2500;

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { isOnboardingRequired, isLoading: onboardingLoading } =
    useOnboarding();
  const router = useRouter();
  const pathname = usePathname();
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const [extendedLoading, setExtendedLoading] = useState(false);

  // Timeout to prevent infinite loading + staged feedback
  useEffect(() => {
    if (authLoading || onboardingLoading) {
      // Show extended message after 2.5s
      const extendedTimer = setTimeout(() => {
        setExtendedLoading(true);
      }, EXTENDED_LOADING_MS);

      // Full timeout after 5s
      const timer = setTimeout(() => {
        console.warn(
          "OnboardingGuard: Loading timed out after",
          LOADING_TIMEOUT_MS,
          "ms",
        );
        setLoadingTimedOut(true);
      }, LOADING_TIMEOUT_MS);

      return () => {
        clearTimeout(timer);
        clearTimeout(extendedTimer);
      };
    } else {
      setLoadingTimedOut(false);
      setExtendedLoading(false);
    }
  }, [authLoading, onboardingLoading]);

  useEffect(() => {
    // Don't redirect while loading (unless timed out)
    if ((authLoading || onboardingLoading) && !loadingTimedOut) return;

    // Don't redirect anonymous users or on public routes
    if (!user || user.isAnonymous || PUBLIC_ROUTES.includes(pathname)) return;

    // Don't redirect if already on onboarding
    if (pathname === "/local-links-v1/onboarding") return;

    // Redirect to onboarding if required and on a protected route
    if (
      isOnboardingRequired &&
      PROTECTED_ROUTES.some((route) => pathname.startsWith(route))
    ) {
      console.log("Redirecting to onboarding from:", pathname);
      router.replace("/local-links-v1/onboarding");
      return;
    }

    // Redirect away from onboarding if not required
    if (!isOnboardingRequired && pathname === "/local-links-v1/onboarding") {
      console.log("Onboarding not required, redirecting to dashboard");
      router.replace("/local-links-v1");
      return;
    }
  }, [
    user,
    authLoading,
    isOnboardingRequired,
    onboardingLoading,
    pathname,
    router,
    loadingTimedOut,
  ]);

  // Show loading state while determining onboarding status (unless timed out)
  if ((authLoading || onboardingLoading) && !loadingTimedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-blue-50">
        <div className="text-center">
          <GradientSurface
            variant="rainbow"
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse"
          >
            <div className="w-8 h-8 bg-white rounded-full"></div>
          </GradientSurface>
          <p className="text-gray-600">
            {extendedLoading
              ? "Still loading... Just a moment more!"
              : "Loading your golf experience..."}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default OnboardingGuard;
